import { Add } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Button, Input, Modal } from "../../../../components";
import { useCategories } from "../../../../hooks/useCategories";
import useDebounce from "../../../../hooks/useDebounce";
import {
  getAllNGO_V2,
  linkNGOsToOrganization,
} from "../../../../Services_V2/NGO.service";
import { INGO_V2 } from "../../../../types";
import { showError } from "../../../../utils/error";

interface LinkNgoPopupProps {
  open: boolean;
  onClose: () => void;
  onLinkSuccess: () => void;
  onAddNgo?: () => void;
  currentNgoIds: string[];
}

const LinkNgoPopup: React.FC<LinkNgoPopupProps> = ({
  open,
  onClose,
  onLinkSuccess,
  currentNgoIds,
  onAddNgo,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [availableNgos, setAvailableNgos] = useState<INGO_V2[]>([]);
  const [selectedNgoIds, setSelectedNgoIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const { map: categoryMap } = useCategories();

  const debouncedSearch = useDebounce(searchTerm, 500);

  const getPayload = useCallback(
    () => ({
      page: 1,
      limit: 100,
      search: debouncedSearch || undefined,
    }),
    [debouncedSearch]
  );

  const fetchAvailableNGOs = useCallback(async () => {
    setLoading(true);
    try {
      const payload = getPayload();
      const res = await getAllNGO_V2(payload);

      const allNgos = res?.data || [];

      setAvailableNgos(allNgos);
    } catch (error) {
      showError(error);
      setAvailableNgos([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentNgoIds]);

  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setSelectedNgoIds(new Set());
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchAvailableNGOs();
    }
  }, [debouncedSearch, open, fetchAvailableNGOs]);

  const handleToggleSelect = (ngoId: string) => {
    setSelectedNgoIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(ngoId)) {
        newSet.delete(ngoId);
      } else {
        newSet.add(ngoId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedNgoIds.size === availableNgos.length) {
      setSelectedNgoIds(new Set());
    } else {
      setSelectedNgoIds(new Set(availableNgos.map((ngo) => ngo._id!)));
    }
  };

  const handleLinkNGOs = async () => {
    if (selectedNgoIds.size === 0) {
      toast.error("Please select at least one NGO to link");
      return;
    }

    try {
      const res = await linkNGOsToOrganization({
        ngos: Array.from(selectedNgoIds),
      });

      if (res?.success) {
        toast.success(res?.message);
      }
      onLinkSuccess();
      onClose();
    } catch (error) {
      showError(error);
    }
  };

  const showAddNgoButton = !loading && searchTerm && availableNgos.length === 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add new NGO or Link Existing NGOs"
      showClose={true}
      modalStyle={{ width: { xs: "90%", sm: 600 } }}
    >
      <Box sx={{ display: "flex", gap: 3, flexDirection: "column" }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Input
            placeholder="Search NGOs by name or darpan Id"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            disabled={loading}
          />
          {showAddNgoButton ? (
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={onAddNgo}
              sx={{
                height: 40,
                minWidth: 140,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              NGO
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleLinkNGOs}
              disabled={selectedNgoIds.size === 0 || loading}
              sx={{
                height: 40,
                minWidth: 140,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                `Link (${selectedNgoIds.size})`
              )}
            </Button>
          )}
        </Box>

        <Box
          sx={{
            maxHeight: "50vh",
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            bgcolor: "#fafafa",
          }}
        >
          {loading && availableNgos.length === 0 ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Loading NGOs...</Typography>
            </Box>
          ) : availableNgos.length === 0 ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <Typography color="text.secondary">
                {searchTerm
                  ? "No NGOs found matching your search."
                  : "No available NGOs to link."}
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  p: 2,
                  borderBottom: "1px solid #e0e0e0",
                  bgcolor: "white",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        availableNgos.length > 0 &&
                        selectedNgoIds.size === availableNgos.length
                      }
                      indeterminate={
                        selectedNgoIds.size > 0 &&
                        selectedNgoIds.size < availableNgos.length
                      }
                      onChange={handleSelectAll}
                    />
                  }
                  label={`Select All (${availableNgos.length})`}
                />
              </Box>

              {availableNgos.map((ngo) => {
                const categoryLabel = categoryMap.get(String(ngo?.category));

                return (
                  <Box
                    key={ngo._id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 2,
                      borderBottom: "1px solid #eee",
                      bgcolor: "white",
                      "&:hover": { bgcolor: "#f5f5f5" },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <Checkbox
                      checked={selectedNgoIds.has(ngo._id!)}
                      onChange={() => handleToggleSelect(ngo._id!)}
                    />
                    <Box sx={{ ml: 2, flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {ngo?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Darpan Id: {ngo?.darpanId}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Category: {categoryLabel && <>{categoryLabel} </>}
                      </Typography>
                    </Box>
                    {ngo.logo && (
                      <Box
                        component="img"
                        src={ngo.logo.url}
                        alt={ngo.name}
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 1,
                          objectFit: "cover",
                          ml: 2,
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default LinkNgoPopup;
